<template>
  <vf-carousel-elevate
    :key="renderKey"
    ref="gallery"
    class="group bg-white"
    :class-container="{ 'h-full': isFocused }"
    :class-controls="['<lg:hidden', carousel.brandClasses?.controls]"
    :class-wrapper="{ 'h-full': isFocused }"
    data-test-id="product-card-gallery"
    :drag-free="false"
    loop
    show-arrows-on-hover-only
    :size-controls="carousel.controls?.iconSize"
    :slides-to-scroll="1"
  >
    <div
      v-for="(item, key) in _items"
      :key
      v-style:aspect-ratio="aspectRatio"
      class="relative w-full"
      :class="{ 'h-full': isFocused }"
    >
      <component
        :is="item.type === 'image' ? BasePicture : BaseVideo"
        v-if="isMediaRendered(key)"
        :ref="(el) => media[key] = isVideo(el) ? el : null"
        v-bind="item"
        class="absolute-0 full cursor-pointer contain"
        :class="[
          { 'bg-black': item.type === 'video' },
          { '[&>img]:absolute-0 [&>img]:full [&>img]:cover': isFocused },
        ]"
        :fit="isFocused ? 'cover' : undefined"
        @click="$emit('click-gallery-item')"
      />
      <slot name="link" />
      <base-button
        v-if="item.type === 'video' && isMediaRendered(key)"
        :aria-label="media[key]?.paused ? $t.videoPlay : $t.videoPause"
        class="absolute right-0 mr-2 p-2 c-grey-60"
        :class="badgePosition === 'bottom' ? 'top-0 mt-2' : 'bottom-0 mb-2'"
        style="mix-blend-mode: difference"
        @click="media[key]?.toggle"
      >
        <vf-icon :name="media[key]?.paused ? 'play' : 'pause'" size="lg" />
      </base-button>
    </div>

    <template #pagination="{ activeItem }">
      <div
        v-if="_items.length"
        class="absolute inset-x-0 bottom-0 z-1 flex duration lg:op-0 group-hover:lg:op-100"
      >
        <div
          v-for="(_, key) in _items"
          :key
          class="grow duration"
          :class="carousel.brandClasses?.[activeItem === key ? 'paginationIndicatorActive' : 'paginationIndicator']"
          style="height: 0.125rem"
        />
      </div>
    </template>
  </vf-carousel-elevate>
</template>

<script lang="ts" setup>
import type { ProductGalleryExtended } from '#root/api/clients/product/data-contracts'
import type { Responsive } from '#types/common'
import { BasePicture, BaseVideo } from '#components'

const props = defineProps<{
  /** CMS asset leading the gallery, per breakpoint - set by a `focused-product-espot`. */
  assetOverride?: Responsive<string>
  /** Whether the card takes a 2x2 focused tile at each breakpoint - see `ProductCard`. */
  focused?: Responsive<boolean>
  items: ProductGalleryExtended
  name: string
}>()

defineEmits<{
  'click-gallery-item': []
}>()

const {
  badgePosition,
  maxCarouselItems,
  maxGalleryItems,
  mediaTransformations,
  carousel
} = useAppConfig().components.product.card

const { $feature } = useNuxtApp()
const gallery = useTemplateRef('gallery')
const media = ref<(null | typeof BaseVideo)[]>([])
const renderKey = ref(0)
const {
  aspectRatio,
  isFocused,
  width: mediaWidth,
  height: mediaHeight,
  transformations: focusedTransformations
} = useProductCardMedia(() => props.focused)
const mediaBox = computed(() => ({ width: mediaWidth.value, height: mediaHeight.value }))
const maxItems = maxGalleryItems ?? maxCarouselItems

/** The override leads the gallery, while retaining the product media that follows it. */
const overrideSlide = computed(() => props.assetOverride
  ? [{
      ...mediaBox.value,
      type: 'image' as const,
      src: getAssetOverrideSrc(props.assetOverride, {
        ...mediaBox.value,
        ...mediaTransformations,
        ...focusedTransformations.value
      }),
      alt: props.name
    }]
  : []
)

const productSlides = computed(() => props.items.map((item, i) => ({
  ...item,
  width: mediaWidth.value,
  height: mediaHeight.value,
  ...(item.type === 'image'
    ? {
        src: getResponsiveImageTransformations(item.src, {
          height: mediaHeight.value,
          width: mediaWidth.value,
          assetTransformation: item.assetTransformation,
          ...mediaTransformations,
          ...focusedTransformations.value
        }),
        alt: item.alt || `${props.name} - ${i + 1}`,
      }
    : {}
  ),
  ...(item.type === 'video'
    ? {
        src: getVideoTransformations(item.src, $feature.configVideoPresets || {}),
        meta: item.meta,
        poster: item.poster,
        loop: true,
        controls: false
      }
    : {}
  )
})))

const _items = computed(() => [...overrideSlide.value, ...productSlides.value].slice(0, maxItems))

const currentIndex = computed(() => gallery.value?.currentIndex ?? 0)
const renderedMediaIndexes = computed(() => {
  const itemCount = _items.value.length
  if (!itemCount) return new Set<number>()

  const activeIndex = currentIndex.value
  return new Set([
    (activeIndex - 1 + itemCount) % itemCount,
    activeIndex,
    (activeIndex + 1) % itemCount,
  ])
})

const isMediaRendered = (index: number) => renderedMediaIndexes.value.has(index)

function isVideo(el: unknown): el is typeof BaseVideo {
  return isObject(el) && 'paused' in el
}

onActivated(() => {
  // Recreate the carousel on keep-alive activation for PLPs.
  renderKey.value += 1
})

watch(currentIndex, (current, previous) => {
  const previousVideo = isVideo(media.value[previous!]) ? media.value[previous!] : null
  if (previousVideo && !previousVideo.paused.value)
    previousVideo.pause()
  media.value[current!]?.play?.()
})
</script>
